/**
 * 座位排布（§11.4）—— 纯函数，零 DOM 读取。
 *
 * 三条设计原则：
 *  1. hero 永远在底部中央，不参与弧线计算；其余对手沿「中心偏上的椭圆弧」均匀分布，
 *     弧穿过正上方、左右对称。
 *  2. `computeSeatSlots` 只输出**舞台百分比**，与舞台像素尺寸无关 —— 这样窗口变化时
 *     left/top 不需要重算，动画只碰 transform（红线 #46）。
 *  3. `resolveTableLayout` 才把百分比换算成像素盒子，并在这一层解决「座位块 / 公共牌 /
 *     底池 / hero 区互相压盖」的问题。动画启动函数直接读它的缓存结果（红线 #47）。
 *
 * 座位数 2..8 全支持（SEAT_LIMITS）。9 人已不在支持范围内，
 * 文档 §11.4 里按 9 人做的几何校验不再适用；本文件按 8 人上限重新校验。
 *
 * ★ 两档形态（docs/DEVICES.md R2）：手机档与**平板档是两套版面常量**，不是一套拉伸。
 *   - 手机档：竖长屏（440×856 基准 / 375×812 下限），弧偏窄、hero 缩在左下角。
 *   - 平板档：接近 1:1 或横向（691×634 实测，折叠机展开态），横向空间富余，
 *     弧要**张得更开**（更大的 rx 与张角）、hero 往中间靠，否则一堆座位挤在中间、
 *     两侧留出两片空地，看起来就是「手机布局被横向拉伸」。
 *   版面常量由 `resolveFrame(stage)` 按舞台形状选，调用方零分支。
 *
 * 校验断言分两份：
 *   - seatGeometry.test.ts：原有机型（375×812 / 430×932）与纯函数性质
 *   - seatGeometry.matrix.test.ts：实测三尺寸 × 2..8 人 = 21 组合的验收矩阵
 */

import { SEAT_LIMITS } from '@poker/shared'
import type { SeatIndex } from '@poker/shared'

// ============================================================================
// 基本几何类型
// ============================================================================

export interface Point {
  readonly x: number
  readonly y: number
}

/** x/y 是左上角，cx/cy 是中心。两者都给是为了少在调用点做算术。 */
export interface Rect {
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
  readonly cx: number
  readonly cy: number
}

export interface StageSize {
  readonly width: number
  readonly height: number
}

export type SeatSide = 'top' | 'left' | 'right' | 'bottom'
export type Density = 'roomy' | 'normal' | 'tight'

/** 形态档位。只有两档，不按机型穷举（docs/DEVICES.md「设备分档」） */
export type ViewportClass = 'phone' | 'tablet'

/** 短边 >= 这个值就是平板档：691×634 的短边 634 由此进入平板档 */
export const TABLET_MIN_SHORT_SIDE = 600
/** 宽高比 >= 这个值算「接近 1:1 或更宽」。691/634 = 1.09 */
export const TABLET_MIN_ASPECT = 0.9
/** 宽屏但不高时，仍要求这个高度才当平板；否则是「手机横过来了」，该提示旋转 */
export const TABLET_MIN_HEIGHT = 520

/** 命中区下限（§11.10），与 tokens.css 的 --hit-min 同值 */
export const MIN_HIT_PX = 44

/**
 * 视口 → 形态档。**平板档绝不提示旋转**（R2）：691×634 既不是竖屏也不是典型横屏，
 * 弹「请竖屏使用」等于把折叠机用户挡在门外。
 */
export function viewportClass(size: StageSize): ViewportClass {
  const short = Math.min(size.width, size.height)
  if (short >= TABLET_MIN_SHORT_SIDE) return 'tablet'
  const aspect = size.height <= 0 ? 0 : size.width / size.height
  if (aspect >= TABLET_MIN_ASPECT && size.height >= TABLET_MIN_HEIGHT) return 'tablet'
  return 'phone'
}

/**
 * 舞台 → 形态档。判据与视口**不同**：舞台已经被 topbar/dock/安全区吃掉一大块高度，
 * 440×856 的手机舞台只有 440×539（比 0.9 还宽），用视口那套判据会把手机误判成平板。
 * 舞台只认两件事：够不够宽（>=600），或者真的比自己高（横过来了）。
 */
export function stageClass(stage: StageSize): ViewportClass {
  if (stage.width >= TABLET_MIN_SHORT_SIDE) return 'tablet'
  return stage.width > stage.height ? 'tablet' : 'phone'
}

// ============================================================================
// 版面常量（百分比坐标系，原点在舞台左上角，y 轴向下）
// ============================================================================

/** 椭圆弧：圆心比舞台中心略偏上，给底部 hero 区留出空间 */
export const RING = { cx: 50, cy: 46, rx: 38, ry: 36 } as const

export const POT_ANCHOR: Point = { x: 50, y: 30 }
export const BOARD_CENTER: Point = { x: 50, y: 43 }
/** 发牌起点：牌盒位置，所有发牌动画从这里飞出 */
export const DECK_POINT: Point = { x: 50, y: 17 }

/** hero 区四个锚点，互不压盖 */
export const HERO_ANCHORS = {
  seat: { x: 18, y: 92 },
  hand: { x: 50, y: 83 },
  bet: { x: 50, y: 70 },
  dealer: { x: 72, y: 74 },
} as const satisfies Record<string, Point>

/**
 * 弧的总张角（±S）。★ 按「弧上座位数 m」索引，不是总人数。
 * m=1 时张角为 0（唯一的对手坐正上方）。越界一律兜底，绝不返回 undefined。
 */
export const ARC_SPAN_BY_M: Readonly<Record<number, number>> = {
  1: 0,
  2: 75,
  3: 90,
  4: 96,
  5: 100,
  6: 106,
  7: 112,
  8: 118,
}

/**
 * 平板档张角：一律比手机档大 20°~26°。
 * 横向空间富余时把弧张开，最外侧的两个座位才会落到舞台左右两边，
 * 而不是和中间几个挤在同一段圆弧上、两侧空出两片草地。
 */
export const ARC_SPAN_BY_M_TABLET: Readonly<Record<number, number>> = {
  1: 0,
  2: 96,
  3: 112,
  4: 118,
  5: 124,
  6: 128,
  7: 132,
  8: 136,
}

/** 兜底取表内最大 m 的值：座位数已被 SEAT_LIMITS 限到 8，这里只是防御 */
const MAX_ARC_M = 8
export function spanForArc(m: number, spans: Readonly<Record<number, number>> = ARC_SPAN_BY_M): number {
  return spans[m] ?? spans[MAX_ARC_M] ?? 118
}

// ============================================================================
// 版面框（两档形态各一份，百分比坐标）
// ============================================================================

export interface Ring {
  readonly cx: number
  readonly cy: number
  readonly rx: number
  readonly ry: number
}

export interface HeroAnchors {
  readonly seat: Point
  readonly hand: Point
  readonly bet: Point
  readonly dealer: Point
}

/**
 * 一整套版面常量。**所有百分比常量都在这里**，别处不许再出现裸数字，
 * 否则加一档形态就要满文件找魔法数。
 */
export interface LayoutFrame {
  readonly klass: ViewportClass
  readonly ring: Ring
  readonly hero: HeroAnchors
  readonly pot: Point
  readonly board: Point
  readonly deck: Point
  readonly spans: Readonly<Record<number, number>>
}

export const PHONE_FRAME: LayoutFrame = {
  klass: 'phone',
  ring: RING,
  hero: HERO_ANCHORS,
  pot: POT_ANCHOR,
  board: BOARD_CENTER,
  deck: DECK_POINT,
  spans: ARC_SPAN_BY_M,
}

/**
 * 平板档版面。与手机档的三处实质差异：
 *  1. `rx` 从 38% 抬到 43%：横向摊开，最外侧座位贴近舞台左右边缘。
 *  2. `ry` 从 36% 压到 31%、`cy` 上移：平板档舞台是扁的（691×412），
 *     椭圆再按 36% 算，顶部座位会顶穿舞台上沿、底部座位会掉进 hero 区。
 *  3. hero 从左下角 (18,92) 挪到 (12,86)：弧的两个末端已经下探到 70% 高度，
 *     hero 必须再往角落收，才不会被最外侧座位压住。
 */
export const TABLET_FRAME: LayoutFrame = {
  klass: 'tablet',
  ring: { cx: 50, cy: 43, rx: 43, ry: 31 },
  hero: {
    seat: { x: 12, y: 86 },
    hand: { x: 50, y: 82 },
    bet: { x: 50, y: 66 },
    dealer: { x: 66, y: 72 },
  },
  pot: { x: 50, y: 26 },
  board: { x: 50, y: 43 },
  deck: { x: 50, y: 13 },
  spans: ARC_SPAN_BY_M_TABLET,
}

/** 舞台形状 → 版面框。调用方（含动画层）零分支，只管读返回值。 */
export function resolveFrame(stage: StageSize): LayoutFrame {
  return stageClass(stage) === 'tablet' ? TABLET_FRAME : PHONE_FRAME
}

// ============================================================================
// 密度
// ============================================================================

export interface DensityMetrics {
  readonly seatW: number
  readonly seatH: number
  /** 公共牌宽度**上限**；真实值由 fitBoardCardWidth 按舞台宽度收窄 */
  readonly boardCardW: number
  readonly heroCardW: number
  readonly heroCardH: number
  readonly dockH: number
  readonly potW: number
  readonly potH: number
}

/**
 * ★ dockH 与 tokens.css 的 `[data-density] --dock-h` 逐档相等（有测试逐档断言）。
 *
 * 2026-09 最终统一到 64px：行动区按 44px 最低触控目标压到可用下限，
 * 快捷档位与滑块不再常驻，改由底部抽屉（`.dock[data-state='sheet']`）浮在牌桌上方。
 * 抽屉是 absolute 浮层，**不参与 flex 布局**，所以展开它不会改变 dock 盒高，
 * 这条恒等式在整手牌里都成立。腾出的约 60px 全部归舞台。
 */
export const DENSITY_METRICS: Readonly<Record<Density, DensityMetrics>> = {
  roomy: { seatW: 76, seatH: 88, boardCardW: 46, heroCardW: 68, heroCardH: 95, dockH: 64, potW: 130, potH: 34 },
  normal: { seatW: 68, seatH: 80, boardCardW: 42, heroCardW: 62, heroCardH: 87, dockH: 64, potW: 120, potH: 32 },
  tight: { seatW: 58, seatH: 70, boardCardW: 36, heroCardW: 54, heroCardH: 76, dockH: 64, potW: 108, potH: 28 },
}

const DENSITY_ORDER: readonly Density[] = ['tight', 'normal', 'roomy']
/** 不动点搜索用：从最宽松往最紧凑扫 */
const DENSITY_ROOMY_FIRST: readonly Density[] = ['roomy', 'normal', 'tight']

function demote(d: Density): Density {
  const i = DENSITY_ORDER.indexOf(d)
  return DENSITY_ORDER[Math.max(0, i - 1)] ?? 'tight'
}

function promote(d: Density): Density {
  const i = DENSITY_ORDER.indexOf(d)
  return DENSITY_ORDER[Math.min(DENSITY_ORDER.length - 1, i + 1)] ?? 'roomy'
}

/** 平板档升档的高度门槛：再矮就是「手机横过来了」，不该把座位块放大 */
const TABLET_PROMOTE_MIN_H = 380

/**
 * 基线密度：只看阈值与三条修正，**不考虑 dock 反馈**。内部用。
 *
 * 三条修正各自最多一档：
 *  - 平板档（舞台够宽且比自己高）**升**一档：691px 宽的屏配 58px 座位块 + 36px 公共牌，
 *    是把手机档原样搬过来的典型症状。横向富余时该用更大的块，这是 R2「一等布局」的实质。
 *  - 布局座位数 >= 7 降一档（§11.2 原规则）
 *  - 舞台宽度 < 360 降一档（比 iPhone SE 还窄，座位块必须让位给公共牌）
 *
 * 三条可以互相抵消（平板 8 人 = 升一档又降一档 = 基线），这是有意的。
 */
function baseDensity(stageH: number, layoutSeatCount: number, stageW?: number): Density {
  let d: Density = stageH >= 500 ? 'roomy' : stageH >= 420 ? 'normal' : 'tight'
  if (
    stageW !== undefined &&
    stageH >= TABLET_PROMOTE_MIN_H &&
    stageClass({ width: stageW, height: stageH }) === 'tablet'
  ) {
    d = promote(d)
  }
  if (layoutSeatCount >= 7) d = demote(d)
  if (stageW !== undefined && stageW < 360) d = demote(d)
  return d
}

/**
 * 由「舞台 + dock」的总预算求密度。**这是唯一的权威解**，
 * `resolveStage` 与 `resolveDensity` 都归约到它。
 *
 * 为什么要枚举而不是迭代：dock 高度依赖密度、密度又依赖舞台高度，互为因果。
 * 691×634（D1 实测）上 normal→tight→normal 会无限来回跳，迭代版本返回的
 * density 与算 height 时用的那一档不是同一档，于是 topbar + 舞台 + dock
 * 比视口高出 16px —— dock 最底下那排按钮被顶出屏幕（DEVICES.md R3）。
 *
 * 没有不动点时取最紧一档：dock 最矮 → 舞台最高 → 绝不溢出视口。
 */
export function densityForBudget(budget: number, layoutSeatCount: number, stageW?: number): Density {
  for (const d of DENSITY_ROOMY_FIRST) {
    if (baseDensity(budget - DENSITY_METRICS[d].dockH, layoutSeatCount, stageW) === d) return d
  }
  return 'tight'
}

/**
 * 由**实测舞台高度**求密度。
 *
 * ★ 这里必须比 `baseDensity` 多绕一圈，否则运行时会自己跟自己打架：
 *   密度 → `--dock-h` → dock 盒高 → 舞台高度 → 密度 …
 *   691×634 上 412px 判 tight、428px 判 normal，两档互相把对方推回去，
 *   牌桌会在两套座位尺寸之间抽搐；更要命的是 CSS 的 `--seat-w` 与几何假设的
 *   `metrics.seatW` 会长期对不上 —— 画出来的块比算出来的大 10px，
 *   「座位块不重叠」这条保证当场失效。
 *
 * 解法：实测高度 H 一定是「总预算 C 减去某一档的 dock」。
 *  - **知道当前生效的是哪一档**（`current`，强烈建议传）：C = H + dock(current)，
 *    直接用 `densityForBudget` 求唯一解，最多两步收敛，且与 `resolveStage` 完全一致。
 *  - 不知道：只能逐档假设并取第一个自洽的（从紧到松扫）。这是**尽力而为**：
 *    仅凭 H 根本无法区分「tight 档量出来的 H」和「normal 档量出来的 H」
 *    （两者可以相等），所以某些视口高度上仍会来回跳。
 *    实测三机型 × 2..8 人已在 seatGeometry.matrix.test.ts 里逐组合验证为稳定，
 *    但**调用方仍应当传 `current`** —— 那条路径是全参数扫描证明过的一步收敛。
 */
export function resolveDensity(
  stageH: number,
  layoutSeatCount: number,
  stageW?: number,
  current?: Density,
): Density {
  if (current !== undefined) {
    return densityForBudget(stageH + DENSITY_METRICS[current].dockH, layoutSeatCount, stageW)
  }
  for (const d of DENSITY_ORDER) {
    if (densityForBudget(stageH + DENSITY_METRICS[d].dockH, layoutSeatCount, stageW) === d) return d
  }
  return densityForBudget(stageH + DENSITY_METRICS.tight.dockH, layoutSeatCount, stageW)
}

/** 壳层高度常量，与 global.css 的 --topbar-h / --dock-h 一致 */
export const TOPBAR_H = 48
export const SAFE_BOTTOM_MIN = 10

export interface ViewportInput {
  readonly width: number
  readonly height: number
  readonly safeTop?: number
  readonly safeBottom?: number
}

export interface StageResolution {
  readonly stage: StageSize
  readonly density: Density
  readonly dockH: number
  readonly topbarH: number
  readonly safeTop: number
  readonly safeBottom: number
  readonly klass: ViewportClass
}

/**
 * 由视口反推舞台尺寸。dock 高度依赖密度、密度又依赖舞台高度 —— 互为因果，
 * 所以这里**直接枚举三档找不动点**，而不是迭代。
 *
 * 为什么不能迭代：691×634（D1 实测）上 normal→tight→normal 会来回跳，
 * 迭代版本最后返回的 `density` 与算 `height` 时用的那一档**不是同一档**，
 * 于是 topbar + 舞台 + dock 比视口高出 16px —— dock 最底下那排按钮被顶出屏幕，
 * 正好命中 DEVICES.md R3「弃牌/跟注点不到」。
 *
 * 没有不动点时取最紧的一档：dock 最矮 → 舞台最高 → 绝不会溢出视口。
 * 无论走哪条分支，返回值都满足
 *   safeTop + TOPBAR_H + stage.height + dockH + safeBottom === vp.height
 * 这条恒等式由 matrix 测试逐组合断言。
 */
export function resolveStage(vp: ViewportInput, layoutSeatCount: number): StageResolution {
  const safeTop = vp.safeTop ?? 0
  const safeBottom = Math.max(vp.safeBottom ?? 0, SAFE_BOTTOM_MIN)
  const shell = safeTop + TOPBAR_H + safeBottom
  const heightFor = (d: Density): number => vp.height - shell - DENSITY_METRICS[d].dockH

  const density = densityForBudget(vp.height - shell, layoutSeatCount, vp.width)

  return {
    stage: { width: vp.width, height: heightFor(density) },
    density,
    dockH: DENSITY_METRICS[density].dockH,
    topbarH: TOPBAR_H,
    safeTop,
    safeBottom,
    klass: viewportClass({ width: vp.width, height: vp.height }),
  }
}

// ============================================================================
// 布局座位数与座位顺序
// ============================================================================

/**
 * 布局输入口径（§11.4）：`clamp(2, max(occupied, 2), maxSeats)`。
 * 调用方必须在 phase !== idle 期间**冻结**这个值，否则一手牌打到一半来人就整桌漂移，
 * 筹码飞行的起止点会跟着变。
 */
export function layoutSeatCount(occupiedSeatCount: number, maxSeats: number): number {
  const cap = Math.min(Math.max(maxSeats, SEAT_LIMITS.MIN), SEAT_LIMITS.MAX)
  return Math.min(Math.max(occupiedSeatCount, SEAT_LIMITS.MIN), cap)
}

/** 生成 0..n-1 的座位号，用于「只有人数、不关心真实座位号」的场景（测试/大厅预览） */
export function seatsForCount(n: number): SeatIndex[] {
  return Array.from({ length: n }, (_, i) => i)
}

/**
 * 把桌面座位号按顺时针排成布局顺序，hero 排在第 0 位。
 * hero 不在列表里（旁观）时原样返回升序。
 */
export function orderSeatsForLayout(
  seats: readonly SeatIndex[],
  heroSeat: SeatIndex | null,
): SeatIndex[] {
  const sorted = [...seats].sort((a, b) => a - b)
  if (heroSeat === null) return sorted
  const at = sorted.indexOf(heroSeat)
  if (at < 0) return sorted
  return [...sorted.slice(at), ...sorted.slice(0, at)]
}

// ============================================================================
// 弧上落点
// ============================================================================

export interface SeatSlot {
  /** 桌面座位号 */
  readonly seat: SeatIndex
  /** 0 = hero（有 hero 时），顺时针递增 */
  readonly layoutIndex: number
  readonly isHero: boolean
  /** 座位块中心（舞台百分比） */
  readonly pos: Point
  /** 下注筹码落点（舞台百分比） */
  readonly betPos: Point
  /** 庄家钮落点（舞台百分比） */
  readonly dealerPos: Point
  readonly side: SeatSide
  /** 弧上角度（度，270 = 正上方）；hero 恒为 90（正下方） */
  readonly angle: number
}

export interface SeatSlotInput {
  /** 参与排布的座位号；顺序无所谓，内部会按座位号升序并旋转到 hero 优先 */
  readonly seats: readonly SeatIndex[]
  /** hero 座位号；null = 旁观视角，全部座位上弧 */
  readonly heroSeat: SeatIndex | null
  /** 版面框；省略按手机档。`resolveTableLayout` 会按舞台形状传对应的一份。 */
  readonly frame?: LayoutFrame
}

const rad = (deg: number): number => (deg * Math.PI) / 180
const lerp = (a: Point, b: Point, t: number): Point => ({
  x: a.x + (b.x - a.x) * t,
  y: a.y + (b.y - a.y) * t,
})

/** 归一到 [0,360) —— 弧角度会超过 360（例如 n=6 的最后一位是 370°） */
function norm360(deg: number): number {
  return ((deg % 360) + 360) % 360
}

function sideForAngle(deg: number): SeatSide {
  const a = norm360(deg)
  if (a >= 225 && a < 315) return 'top'
  if (a >= 135 && a < 225) return 'left'
  if (a >= 315 || a < 45) return 'right'
  return 'bottom'
}

/** 弧上第 j 个（共 m 个）的角度。m===1 时坐正上方。 */
export function arcAngle(j: number, m: number, spans: Readonly<Record<number, number>> = ARC_SPAN_BY_M): number {
  if (m <= 1) return 270
  const S = spanForArc(m, spans)
  return 270 - S + (j * (2 * S)) / (m - 1)
}

const DEALER_TANGENT_OFFSET = 4

/**
 * 计算每个座位的百分比锚点。返回顺序即 layoutIndex 顺序（hero 在最前）。
 *
 * betPos 只做「向底池插值 34%」；与公共牌包围盒的避让放在 resolveTableLayout 里做，
 * 因为公共牌的真实尺寸要等舞台宽度定下来才知道。
 */
export function computeSeatSlots(input: SeatSlotInput): SeatSlot[] {
  const frame = input.frame ?? PHONE_FRAME
  const ring = frame.ring
  const ordered = orderSeatsForLayout(input.seats, input.heroSeat)
  if (ordered.length === 0) return []
  const hasHero = input.heroSeat !== null && ordered[0] === input.heroSeat
  const arcCount = hasHero ? ordered.length - 1 : ordered.length

  const out: SeatSlot[] = []
  for (let layoutIndex = 0; layoutIndex < ordered.length; layoutIndex++) {
    const seat = ordered[layoutIndex]
    if (seat === undefined) continue

    if (hasHero && layoutIndex === 0) {
      out.push({
        seat,
        layoutIndex,
        isHero: true,
        pos: frame.hero.seat,
        betPos: frame.hero.bet,
        dealerPos: frame.hero.dealer,
        side: 'bottom',
        angle: 90,
      })
      continue
    }

    const j = hasHero ? layoutIndex - 1 : layoutIndex
    const angle = arcAngle(j, arcCount, frame.spans)
    const pos: Point = {
      x: ring.cx + ring.rx * Math.cos(rad(angle)),
      y: ring.cy + ring.ry * Math.sin(rad(angle)),
    }
    // 切线方向（角度增大 = 顺时针）：庄家钮压在顺时针一侧，指向小盲
    const tangent: Point = { x: -Math.sin(rad(angle)), y: Math.cos(rad(angle)) }
    const dealerBase = lerp(pos, frame.pot, 0.2)
    out.push({
      seat,
      layoutIndex,
      isHero: false,
      pos,
      betPos: lerp(pos, frame.pot, 0.34),
      dealerPos: {
        x: dealerBase.x + tangent.x * DEALER_TANGENT_OFFSET,
        y: dealerBase.y + tangent.y * DEALER_TANGENT_OFFSET,
      },
      side: sideForAngle(angle),
      angle,
    })
  }
  return out
}

// ============================================================================
// 矩形工具
// ============================================================================

export function makeRect(cx: number, cy: number, width: number, height: number): Rect {
  return { x: cx - width / 2, y: cy - height / 2, width, height, cx, cy }
}

export function inflate(r: Rect, m: number): Rect {
  return makeRect(r.cx, r.cy, r.width + m * 2, r.height + m * 2)
}

/** 严格相交：只碰边不算重叠 */
export function rectsOverlap(a: Rect, b: Rect, margin = 0): boolean {
  return (
    a.x - margin < b.x + b.width &&
    b.x < a.x + a.width + margin &&
    a.y - margin < b.y + b.height &&
    b.y < a.y + a.height + margin
  )
}

export function rectContains(outer: StageSize, r: Rect, epsilon = 0.5): boolean {
  return (
    r.x >= -epsilon &&
    r.y >= -epsilon &&
    r.x + r.width <= outer.width + epsilon &&
    r.y + r.height <= outer.height + epsilon
  )
}

/** 把矩形夹进舞台；比舞台还大时居中（宁可溢出对称也不要贴边） */
function clampRect(r: Rect, stage: StageSize): Rect {
  const cx =
    r.width <= stage.width
      ? Math.min(Math.max(r.cx, r.width / 2), stage.width - r.width / 2)
      : stage.width / 2
  const cy =
    r.height <= stage.height
      ? Math.min(Math.max(r.cy, r.height / 2), stage.height - r.height / 2)
      : stage.height / 2
  return makeRect(cx, cy, r.width, r.height)
}

export function pointToPx(p: Point, stage: StageSize): Point {
  return { x: (p.x / 100) * stage.width, y: (p.y / 100) * stage.height }
}

/** 点是否落在矩形内（含 pad 的外扩） */
function pointInRect(p: Point, r: Rect, pad = 0): boolean {
  return (
    p.x > r.x - pad && p.x < r.x + r.width + pad && p.y > r.y - pad && p.y < r.y + r.height + pad
  )
}

function clampPoint(p: Point, stage: StageSize, pad: number): Point {
  return {
    x: Math.min(Math.max(p.x, pad), stage.width - pad),
    y: Math.min(Math.max(p.y, pad), stage.height - pad),
  }
}

// ============================================================================
// 标记点（下注筹码 / 庄家钮）的落位
// ============================================================================

/**
 * 标记的名义尺寸。
 *
 * 为什么必须按**盒子**而不是按**点**来避让：`.bet-chip` 与 `.dealer-btn` 都是
 * `.stage-anchor`（translate(-50%,-50%)），锚点是它们的中心。只保证「中心不在
 * 别人盒子里」，等于允许一半的筹码压在座位块或公共牌上 —— 实测 440×856 的
 * 2..8 人**每一档**都能看到下注筹码骑在邻座头像上、第 5 张公共牌上。
 *
 * 宽度取 `formatChips` 的最坏情况：它把大数压成 "999.9K" / "9.9M"，
 * 六个字符时 `.bet-chip` 实测约 70px，留 2px 余量。
 */
const BET_MARKER_W = 72
const BET_MARKER_H = 22
/** `.dealer-btn` 是固定 22×22 的圆 */
const DEALER_MARKER = 22

/**
 * 候选方向（相对基准方向的偏转角，度）。先试正前方，再左右交替张开。
 * 顺序即优先级：能放在「座位 → 底池」这条线上就绝不往旁边挪，
 * 否则筹码的位置与「这是谁下的」之间的对应关系会变弱。
 */
const MARKER_ANGLE_STEPS: readonly number[] = [
  0, 18, -18, 36, -36, 54, -54, 74, -74, 96, -96, 122, -122, 150, -150, 180,
]

/** 沿方向外推的步长与最大步数：4px × 18 = 72px，够跨过一个座位块的半宽 */
const MARKER_STEP_PX = 4
const MARKER_MAX_STEPS = 18

/**
 * 求一个不与任何障碍物重叠的标记中心。
 *
 * 用 Minkowski 的老办法把「盒子 vs 盒子」化简成「点 vs 外扩后的盒子」：
 * 尺寸为 S 的标记不与矩形 B 相交 ⟺ 它的中心落在 B 外扩 S/2 之后的矩形之外。
 * 于是只要沿若干候选方向扫描中心点即可，不需要做真正的碰撞解算。
 *
 * 一个都找不到时（极小屏 + 8 人）返回**穿透面积最小**的那个候选，
 * 而不是原地不动：宁可压住一点点，也不要整块糊在头像上。
 */
function placeMarker(
  origin: Rect,
  baseAngle: number,
  size: { readonly w: number; readonly h: number },
  obstacles: readonly Rect[],
  stage: StageSize,
  pad: number,
): Point {
  // 中心点必须避开的区域：障碍物各自外扩半个标记 + pad
  const grown = obstacles.map((o) =>
    makeRect(o.cx, o.cy, o.width + size.w + pad * 2, o.height + size.h + pad * 2),
  )
  // 起步距离：先走出自己的座位块（origin 也在 obstacles 里，这里只是为了少扫几步）
  const selfGrown = makeRect(
    origin.cx,
    origin.cy,
    origin.width + size.w + pad * 2,
    origin.height + size.h + pad * 2,
  )

  let best: Point | null = null
  let bestPenalty = Number.POSITIVE_INFINITY

  for (const delta of MARKER_ANGLE_STEPS) {
    const ang = baseAngle + rad(delta)
    const ux = Math.cos(ang)
    const uy = Math.sin(ang)
    // 沿这个方向走出 selfGrown 的最小距离
    const tx = Math.abs(ux) < 1e-6 ? Number.POSITIVE_INFINITY : selfGrown.width / 2 / Math.abs(ux)
    const ty = Math.abs(uy) < 1e-6 ? Number.POSITIVE_INFINITY : selfGrown.height / 2 / Math.abs(uy)
    const t0 = Math.min(tx, ty)

    for (let step = 0; step <= MARKER_MAX_STEPS; step++) {
      const t = t0 + step * MARKER_STEP_PX
      const raw = { x: origin.cx + ux * t, y: origin.cy + uy * t }
      // 夹进舞台（按标记盒子的半宽半高，而不是一个固定的边距）
      const c = clampPoint(raw, stage, 0)
      const box = clampRect(makeRect(c.x, c.y, size.w, size.h), stage)
      const p = { x: box.cx, y: box.cy }

      let penalty = 0
      for (const g of grown) {
        if (!pointInRect(p, g)) continue
        // 穿透深度用「离最近边的距离」度量，越深罚分越高
        const dx = Math.min(p.x - g.x, g.x + g.width - p.x)
        const dy = Math.min(p.y - g.y, g.y + g.height - p.y)
        penalty += Math.min(dx, dy)
      }
      if (penalty === 0) return p
      if (penalty < bestPenalty) {
        bestPenalty = penalty
        best = p
      }
    }
  }
  return best ?? { x: origin.cx, y: origin.cy }
}

// ============================================================================
// 像素布局
// ============================================================================

export interface BoardLayout {
  /** 5 张公共牌的整体包围盒 */
  readonly box: Rect
  readonly cardW: number
  readonly cardH: number
  readonly gap: number
  /** 5 个牌位，索引 0..4 = flop1..river */
  readonly slots: readonly Rect[]
}

/**
 * 聊天气泡的落点（牌桌嘴炮）。`x/y` 是小尾巴的尖，气泡朝 `dir` 方向长出去；
 * 上下方向时 `align` 决定气泡相对尖端怎么摆（贴边的座位不能居中，会出舞台）。
 *
 * `clean = false` 表示四个方向都会压到东西，只好退回首选方向 —— 聊天层在所有牌局元素
 * **之下**，所以这时是气泡自己被挡一角，而不是它挡住牌。
 */
export interface SayAnchor {
  readonly x: number
  readonly y: number
  readonly dir: 'up' | 'down' | 'left' | 'right'
  readonly align: 'start' | 'center' | 'end'
  readonly clean: boolean
  /**
   * 这个落点上气泡最宽能到多少还不出舞台（talk.css 的 --say-max）。
   * 落点是按估算宽度 `SAY_BOX.w` 挑的，手打的长句实际能到 `SAY_MAX_W`；
   * 贴边的座位给不了那么宽，气泡就在这个宽度内截断。
   */
  readonly maxW: number
}

export interface SeatAnchor {
  readonly seat: SeatIndex
  readonly layoutIndex: number
  readonly isHero: boolean
  readonly side: SeatSide
  /** 座位块（已夹进舞台） */
  readonly box: Rect
  /** 座位块中心，等于 box.cx/cy —— 发牌与推池动画的终点 */
  readonly center: Point
  readonly bet: Point
  readonly dealer: Point
  /**
   * 行动气泡该画在座位块**下方**。
   * 气泡默认在座位块上沿之上约 26px，而弧顶的座位块离舞台上沿只有十几像素 ——
   * `.stage` 是 `overflow:hidden`，实测 440×856 的 2 人/4 人局里
   * 正上方那位的「加注到 200」被从中间横着切掉一截。
   */
  readonly bubbleBelow: boolean
  /**
   * 摊牌后的牌型文案该画在座位块**下方**（false 时画在上方）。
   * 弧的两端会下探到舞台 70% 以下，平板档尤甚；下方放不下就必须翻上去，
   * 否则同样被 overflow:hidden 切掉。
   */
  readonly handLabelBelow: boolean
  /** 聊天气泡落点（见 `SayAnchor`） */
  readonly say: SayAnchor
}

export interface TableLayout {
  readonly stage: StageSize
  readonly density: Density
  readonly metrics: DensityMetrics
  /** 本次排布用的版面框（含形态档）。渲染层据此切 data-viewport，不必自己再判一次。 */
  readonly frame: LayoutFrame
  /** layoutIndex 顺序；有 hero 时 seats[0].isHero === true */
  readonly seats: readonly SeatAnchor[]
  readonly seatBySeat: ReadonlyMap<SeatIndex, SeatAnchor>
  /** hero 手牌区（没有 hero 时为 null） */
  readonly heroHand: Rect | null
  readonly pot: Rect
  readonly board: BoardLayout
  /** 发牌起点 */
  readonly deck: Point
}

export interface TableLayoutInput {
  readonly seats: readonly SeatIndex[]
  readonly heroSeat: SeatIndex | null
  readonly stage: StageSize
  /** 省略则按 resolveDensity(stage.height, seats.length, stage.width, currentDensity) */
  readonly density?: Density
  /**
   * 当前 CSS 生效的密度档（`.app-shell[data-density]`）。
   * 调用方**应当**传：舞台高度是在这一档的 dock 下量出来的，
   * 有了它才能还原总预算、得到与 `resolveStage` 一致的唯一解（见 `resolveDensity`）。
   */
  readonly currentDensity?: Density
  /** 省略则按 resolveFrame(stage)。只有测试需要强指定。 */
  readonly frame?: LayoutFrame
}

/** 下注筹码 / 庄家钮离座位块外沿的距离 */
const MARKER_PAD = 6
/**
 * 庄家钮相对「座位→底池」方向的偏转角（顺时针为正，指向小盲一侧）。
 * 用**转方向**而不是「先投影再平移」：平移会把已经贴到外沿的点又推回座位块里。
 */
const DEALER_ANGLE_RAD = (26 * Math.PI) / 180
/**
 * 行动气泡需要的头顶净空（`.seat__bubble-wrap` 的 bottom:100%+4px + 气泡自身约 22px）。
 * 不够就翻到座位块下方。
 */
const BUBBLE_HEADROOM = 28
/** 摊牌牌型文案需要的脚下净空（`.seat__hand` 的 top:100%+3px + 文案约 19px） */
const HAND_LABEL_H = 24

/**
 * 聊天气泡的估算盒（talk.css 的 `.say`）。宽度按一条 5 字短语估（13px × 5 + 内边距），
 * 手打的长句会更宽 —— 但聊天层在牌局元素之下，估窄了的代价只是自己被挡一截。
 */
export const SAY_BOX = { w: 96, h: 24 } as const
/** 尾巴长度 + 离座位块的空隙 */
const SAY_TAIL = 5
const SAY_GAP = 2
/** 贴边对齐时尾巴离气泡边缘的距离（talk.css 的 --say-tail-inset 同值） */
export const SAY_TAIL_INSET = 16
/** 气泡的最大宽度（手打的长句）；talk.css 的 .say__body 回退值同值 */
export const SAY_MAX_W = 132
/** 离舞台边缘至少留这么多 */
const SAY_EDGE = 4

const SAY_ORDER: Readonly<Record<SeatSide, readonly SayAnchor['dir'][]>> = {
  // 两侧的座位朝桌心开口：不和头顶的行动气泡抢位置，也最像「对着桌上的人说」
  left: ['right', 'up', 'down', 'left'],
  right: ['left', 'up', 'down', 'right'],
  top: ['down', 'right', 'left', 'up'],
  bottom: ['up', 'right', 'left', 'down'],
}

/** 聊天气泡的矩形（默认按估算宽度）。渲染层与测试都用它，二者不会各算各的。 */
export function sayRect(a: Pick<SayAnchor, 'x' | 'y' | 'dir' | 'align'>, w: number = SAY_BOX.w): Rect {
  const { h } = SAY_BOX
  switch (a.dir) {
    case 'right':
      return { x: a.x + SAY_TAIL, y: a.y - h / 2, width: w, height: h, cx: a.x + SAY_TAIL + w / 2, cy: a.y }
    case 'left':
      return { x: a.x - SAY_TAIL - w, y: a.y - h / 2, width: w, height: h, cx: a.x - SAY_TAIL - w / 2, cy: a.y }
    case 'up':
    case 'down': {
      const top = a.dir === 'up' ? a.y - SAY_TAIL - h : a.y + SAY_TAIL
      const left =
        a.align === 'center' ? a.x - w / 2 : a.align === 'start' ? a.x - SAY_TAIL_INSET : a.x + SAY_TAIL_INSET - w
      return { x: left, y: top, width: w, height: h, cx: left + w / 2, cy: top + h / 2 }
    }
  }
}

function sayCandidate(box: Rect, dir: SayAnchor['dir'], stage: StageSize): Omit<SayAnchor, 'clean' | 'maxW'> {
  switch (dir) {
    case 'right':
      return { x: box.x + box.width + SAY_GAP, y: box.cy, dir, align: 'start' }
    case 'left':
      return { x: box.x - SAY_GAP, y: box.cy, dir, align: 'end' }
    case 'up':
    case 'down': {
      const x = box.cx
      const y = dir === 'up' ? box.y - SAY_GAP : box.y + box.height + SAY_GAP
      const half = SAY_BOX.w / 2
      const align: SayAnchor['align'] =
        x - half >= SAY_EDGE && x + half <= stage.width - SAY_EDGE ? 'center' : x - half < SAY_EDGE ? 'start' : 'end'
      return { x, y, dir, align }
    }
  }
}

/** 这个落点往它长出去的方向，最宽还能放多少（不出舞台）。不低于估算宽度 —— 落点本来就按它挑的 */
function sayRoom(c: Omit<SayAnchor, 'clean' | 'maxW'>, stage: StageSize): number {
  const right = stage.width - SAY_EDGE
  let room: number
  switch (c.dir) {
    case 'right':
      room = right - (c.x + SAY_TAIL)
      break
    case 'left':
      room = c.x - SAY_TAIL - SAY_EDGE
      break
    default:
      room =
        c.align === 'center'
          ? 2 * Math.min(c.x - SAY_EDGE, right - c.x)
          : c.align === 'start'
            ? right - (c.x - SAY_TAIL_INSET)
            : c.x + SAY_TAIL_INSET - SAY_EDGE
  }
  return Math.max(SAY_BOX.w, Math.min(SAY_MAX_W, Math.floor(room)))
}

function sayFits(r: Rect, stage: StageSize): boolean {
  return (
    r.x >= SAY_EDGE &&
    r.y >= SAY_EDGE &&
    r.x + r.width <= stage.width - SAY_EDGE &&
    r.y + r.height <= stage.height - SAY_EDGE
  )
}

/**
 * 按座位所在的一侧依次试四个方向，取第一个「不出舞台、不压任何东西」的；
 * 全不行就退回首选方向（`clean: false`）。纯函数，随布局缓存（红线 #47，零 DOM 量测）。
 *
 * 障碍分两档：`hard`（座位块、公共牌、底池、手牌区）永远要避开；`soft`（下注筹码位）
 * 先避开，四个方向都不行时再忽略它们找一遍 —— 下注筹码只在这条街有人下注时才出现，
 * 而且画在聊天层**之上**，压到它的代价只是气泡被筹码挡一角。
 */
export function placeSay(
  box: Rect,
  side: SeatSide,
  hard: readonly Rect[],
  stage: StageSize,
  soft: readonly Rect[] = [],
): SayAnchor {
  const order = SAY_ORDER[side]
  for (const obstacles of [[...hard, ...soft], hard]) {
    for (const dir of order) {
      const c = sayCandidate(box, dir, stage)
      const r = sayRect(c)
      if (sayFits(r, stage) && !obstacles.some((o) => rectsOverlap(r, o))) {
        return { ...c, clean: true, maxW: sayRoom(c, stage) }
      }
    }
    if (soft.length === 0) break
  }
  const fallback = sayCandidate(box, order[0] ?? 'up', stage)
  return { ...fallback, clean: false, maxW: sayRoom(fallback, stage) }
}

const BOARD_GAP = 5
/** 公共牌宽度下限：再窄点数就读不出来了 */
export const BOARD_CARD_MIN_W = 30
/** 公共牌与座位块之间必须留的净空 */
const BOARD_CLEARANCE = 4
/** 公共牌与舞台左右边缘的净空 */
const BOARD_EDGE_GUTTER = 4
const CARD_ASPECT = 1.4
/** hero 两张底牌是错开叠放的，整体宽约单张的 1.7 倍 */
const HERO_HAND_W_RATIO = 1.7

const boardWidthFor = (cardW: number): number => cardW * 5 + BOARD_GAP * 4

/**
 * 选出不与任何座位块打架的最大公共牌宽度。
 *
 * 为什么必须动态算：375px 宽的屏上，roomy 的 46px 公共牌横排要占 254px，
 * 左右只剩 60px，而 roomy 座位块就有 76px —— 侧位座位块必然压在公共牌上。
 * 文档 §11.4 只校验了「座位 vs 座位」和「座位 vs hero」，漏了这一条。
 * 宽高都随 cardW 单调增，所以从上限往下线性扫第一个不冲突的值即最优解。
 */
function fitBoardCardWidth(
  maxCardW: number,
  center: Point,
  stage: StageSize,
  obstacles: readonly Rect[],
): number {
  for (let w = Math.floor(maxCardW); w >= BOARD_CARD_MIN_W; w--) {
    const box = makeRect(center.x, center.y, boardWidthFor(w), Math.round(w * CARD_ASPECT))
    if (box.width > stage.width - BOARD_EDGE_GUTTER * 2) continue
    if (obstacles.some((o) => rectsOverlap(o, box, BOARD_CLEARANCE))) continue
    return w
  }
  return BOARD_CARD_MIN_W
}

function buildBoard(cardW: number, center: Point): BoardLayout {
  const cardH = Math.round(cardW * CARD_ASPECT)
  const box = makeRect(center.x, center.y, boardWidthFor(cardW), cardH)
  const slots: Rect[] = []
  for (let i = 0; i < 5; i++) {
    slots.push(makeRect(box.x + cardW / 2 + i * (cardW + BOARD_GAP), center.y, cardW, cardH))
  }
  return { box, cardW, cardH, gap: BOARD_GAP, slots }
}

/**
 * 百分比锚点 → 像素盒子。这是**唯一**需要知道舞台像素尺寸的地方，
 * `useTableGeometry` 在 ResizeObserver 回调里调它一次并缓存结果，
 * 动画启动函数只读缓存，全程零 getBoundingClientRect（红线 #47）。
 */
export function resolveTableLayout(input: TableLayoutInput): TableLayout {
  const { stage } = input
  const frame = input.frame ?? resolveFrame(stage)
  const slots = computeSeatSlots({ seats: input.seats, heroSeat: input.heroSeat, frame })
  const density =
    input.density ??
    resolveDensity(
      stage.height,
      Math.max(slots.length, SEAT_LIMITS.MIN),
      stage.width,
      input.currentDensity,
    )
  const metrics = DENSITY_METRICS[density]

  // 1) 座位块：百分比 → 像素，再夹进舞台
  const boxes = slots.map((s) => {
    const c = pointToPx(s.pos, stage)
    return clampRect(makeRect(c.x, c.y, metrics.seatW, metrics.seatH), stage)
  })

  // 2) hero 手牌区
  const heroSlot = slots[0]?.isHero === true ? slots[0] : null
  const heroHand =
    heroSlot === null
      ? null
      : (() => {
          const c = pointToPx(frame.hero.hand, stage)
          return clampRect(
            makeRect(
              c.x,
              c.y,
              Math.round(metrics.heroCardW * HERO_HAND_W_RATIO),
              metrics.heroCardH,
            ),
            stage,
          )
        })()

  // 3) 底池
  const potCenter = pointToPx(frame.pot, stage)
  const pot = clampRect(makeRect(potCenter.x, potCenter.y, metrics.potW, metrics.potH), stage)

  // 4) 公共牌：让位给座位块与 hero 手牌
  const boardCenter = pointToPx(frame.board, stage)
  const obstacles: Rect[] = [...boxes]
  if (heroHand !== null) obstacles.push(heroHand)
  const board = buildBoard(
    fitBoardCardWidth(metrics.boardCardW, boardCenter, stage, obstacles),
    boardCenter,
  )

  // 5) 下注位与庄家钮
  //    ★ 按**盒子**避让，不是按点（见 placeMarker 的注释）。
  //    障碍物 = 所有座位块 + 公共牌 + 底池 + hero 手牌区 + 已经放好的标记。
  //    「已经放好的标记」也算障碍，两个相邻座位的筹码才不会叠在一起。
  const potPx = { x: pot.cx, y: pot.cy }
  const staticObstacles: Rect[] = [...boxes, board.box, pot]
  if (heroHand !== null) staticObstacles.push(heroHand)

  const resolvedBoxes = slots.map(
    (_, i) => boxes[i] ?? clampRect(makeRect(0, 0, metrics.seatW, metrics.seatH), stage),
  )
  /** 基准方向恒为「座位 → 底池」：筹码往桌心走才读得出「这是下到池子里的」 */
  const toPot = resolvedBoxes.map((b) => Math.atan2(potPx.y - b.cy, potPx.x - b.cx))

  // 第一遍：下注筹码。全场可以同时有 8 个，所以它们互为障碍，逐个累加。
  const betRects: Rect[] = []
  const bets = resolvedBoxes.map((box, i) => {
    const p = placeMarker(
      box,
      toPot[i] ?? 0,
      { w: BET_MARKER_W, h: BET_MARKER_H },
      [...staticObstacles, ...betRects],
      stage,
      MARKER_PAD,
    )
    betRects.push(makeRect(p.x, p.y, BET_MARKER_W, BET_MARKER_H))
    return p
  })

  // 第二遍：庄家钮。全场**只有一个**，所以各座位的庄家位互不冲突，
  // 只需要避开静态障碍与所有下注筹码 —— 一起累加会平白把它挤到奇怪的地方。
  const dealers = resolvedBoxes.map((box, i) =>
    placeMarker(
      box,
      (toPot[i] ?? 0) + DEALER_ANGLE_RAD,
      { w: DEALER_MARKER, h: DEALER_MARKER },
      [...staticObstacles, ...betRects],
      stage,
      MARKER_PAD,
    ),
  )

  // 6) 聊天气泡：必须避开别的座位块、公共牌、底池、hero 手牌区；下注筹码位尽量避开。
  //    庄家钮不算障碍：全场只有一个、位置随按钮轮转，而且画在聊天层之上 ——
  //    按位置算的话，每个座位头顶/身侧那块几乎都被「可能出现的庄家钮」占掉了。
  const says = resolvedBoxes.map((box, i) => {
    const hard: Rect[] = [board.box, pot]
    if (heroHand !== null) hard.push(heroHand)
    resolvedBoxes.forEach((other, j) => {
      if (j !== i) hard.push(other)
    })
    return placeSay(box, slots[i]?.side ?? 'top', hard, stage, betRects)
  })

  const seats: SeatAnchor[] = slots.map((s, i) => {
    const box = resolvedBoxes[i] ?? clampRect(makeRect(0, 0, metrics.seatW, metrics.seatH), stage)
    return {
      seat: s.seat,
      layoutIndex: s.layoutIndex,
      isHero: s.isHero,
      side: s.side,
      box,
      center: { x: box.cx, y: box.cy },
      bet: bets[i] ?? { x: box.cx, y: box.cy },
      dealer: dealers[i] ?? { x: box.cx, y: box.cy },
      bubbleBelow: box.y < BUBBLE_HEADROOM,
      // hero 恒贴舞台底边，它「下方」那块空间其实是操作区（.dock）。
      // 只按 stage.height 判会算出 true，文案要么被操作区盖住、要么被 .stage 的
      // overflow:hidden 从中间切掉 —— 而摊牌时这行字正是玩家最需要看清的东西。
      handLabelBelow: !s.isHero && box.y + box.height + HAND_LABEL_H <= stage.height,
      say: says[i] ?? placeSay(box, s.side, [], stage),
    }
  })

  const seatBySeat = new Map<SeatIndex, SeatAnchor>()
  for (const a of seats) seatBySeat.set(a.seat, a)

  return {
    stage,
    density,
    metrics,
    frame,
    seats,
    seatBySeat,
    heroHand,
    pot,
    board,
    deck: pointToPx(frame.deck, stage),
  }
}
